class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('change', this.onArtskoolMembershipChange.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  async getArtskoolCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  }

  getArtskoolMembershipLines(cart, variantId) {
    return Array.isArray(cart?.items)
      ? cart.items.filter((item) => String(item.variant_id) === String(variantId))
      : [];
  }

  async normalizeArtskoolMembership(variantId, preferredSellingPlanId, removeAll = false) {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await this.getArtskoolCart();
    const lines = this.getArtskoolMembershipLines(cart, variantId);
    if (!lines.length) return null;

    const keep = lines.find((item) => String(item.selling_plan_allocation?.selling_plan?.id || '') === String(preferredSellingPlanId)) || lines[0];
    const updates = {};
    lines.forEach((item) => {
      updates[item.key] = removeAll ? 0 : (item.key === keep.key ? 1 : 0);
    });

    const sections = this.getSectionsToRender().map((section) => section.id).join(',');
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
    });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || state.message || 'Membership could not be updated.');
    return state;
  }

  async onArtskoolMembershipChange(event) {
    const toggle = event.target.closest('[data-artskool-cart-membership-toggle]');
    if (!toggle) return;

    const wrapper = toggle.closest('[data-artskool-cart-membership]');
    const error = wrapper?.querySelector('[data-artskool-cart-membership-error]');
    const wasChecked = !toggle.checked;
    toggle.disabled = true;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const variantId = toggle.dataset.variantId;
      const sellingPlanId = toggle.dataset.sellingPlanId;
      let state;

      if (toggle.checked) {
        const cart = await this.getArtskoolCart();
        const existing = this.getArtskoolMembershipLines(cart, variantId);
        if (existing.length) {
          state = await this.normalizeArtskoolMembership(variantId, sellingPlanId, false);
        } else {
          const sections = this.getSectionsToRender().map((section) => section.id).join(',');
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity: 1, selling_plan: Number(sellingPlanId) }],
              sections,
              sections_url: window.location.pathname,
            }),
          });
          state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || state.message || 'Membership could not be added.');
        }
      } else {
        state = await this.normalizeArtskoolMembership(variantId, sellingPlanId, true);
        if (!state) throw new Error('Membership cart line could not be found.');
      }

      this.renderContents(state);
    } catch (message) {
      toggle.checked = wasChecked;
      toggle.disabled = false;
      wrapper?.classList.toggle('is-active', toggle.checked);
      if (error) {
        error.textContent = message instanceof Error ? message.message : 'Membership could not be updated.';
        error.hidden = false;
      }
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
